// Agency Risk Center - At-risk clients broken down by agent
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ChevronRight, ChevronDown, Phone, Mail,
  Users, Filter, RefreshCw, ArrowLeft, Clock, TrendingUp,
  User, Building2, Search, X
} from 'lucide-react';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// Risk level configurations
const RISK_LEVELS = {
  critical: { label: 'Critical', color: 'bg-red-600', textColor: 'text-red-600', bgLight: 'bg-red-50', range: '85-100' },
  high: { label: 'High', color: 'bg-orange-500', textColor: 'text-orange-600', bgLight: 'bg-orange-50', range: '65-84' },
  elevated: { label: 'Elevated', color: 'bg-yellow-500', textColor: 'text-yellow-600', bgLight: 'bg-yellow-50', range: '50-64' },
  moderate: { label: 'Moderate', color: 'bg-blue-500', textColor: 'text-blue-600', bgLight: 'bg-blue-50', range: '35-49' },
  low: { label: 'Low', color: 'bg-green-400', textColor: 'text-green-600', bgLight: 'bg-green-50', range: '20-34' },
  stable: { label: 'Stable', color: 'bg-green-600', textColor: 'text-green-700', bgLight: 'bg-green-50', range: '0-19' }
};

// Get risk level from score
function getRiskLevel(score) {
  if (score >= 85) return 'critical';
  if (score >= 65) return 'high';
  if (score >= 50) return 'elevated';
  if (score >= 35) return 'moderate';
  if (score >= 20) return 'low';
  return 'stable';
}

// Agent Risk Card - expandable to show clients
function AgentRiskCard({ agent, selectedLevel, onViewClients }) {
  const [expanded, setExpanded] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const atRiskCount = (agent.riskBreakdown?.critical || 0) +
                     (agent.riskBreakdown?.high || 0) +
                     (agent.riskBreakdown?.elevated || 0);

  const loadClients = async () => {
    if (clients.length > 0) {
      setExpanded(!expanded);
      return;
    }

    setLoadingClients(true);
    try {
      const level = selectedLevel || 'all';
      const res = await fetch(
        `${BASE}/agency/at-risk-clients?agentId=${agent.agentId}&riskLevel=${level}&limit=20`,
        { credentials: 'include' }
      );
      const data = await res.json();
      if (data.ok) {
        setClients(data.clients);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setLoadingClients(false);
      setExpanded(true);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
      {/* Agent Header */}
      <button
        onClick={loadClients}
        className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#172A3A] to-[#20344A] flex items-center justify-center text-white font-bold text-lg">
          {agent.agentName?.charAt(0) || '?'}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900">{agent.agentName}</div>
          <div className="text-sm text-gray-500">{agent.agentEmail}</div>
        </div>

        {/* Risk breakdown mini */}
        <div className="hidden sm:flex items-center gap-2">
          {agent.riskBreakdown?.critical > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
              {agent.riskBreakdown.critical} critical
            </span>
          )}
          {agent.riskBreakdown?.high > 0 && (
            <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              {agent.riskBreakdown.high} high
            </span>
          )}
        </div>

        <div className="text-right">
          <div className="text-lg font-bold text-gray-900">{agent.totalClients}</div>
          <div className="text-xs text-gray-500">total clients</div>
        </div>

        <div className={`p-2 rounded-lg transition-transform ${expanded ? 'rotate-180' : ''}`}>
          <ChevronDown size={20} className="text-gray-400" />
        </div>
      </button>

      {/* Expanded client list */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          {loadingClients ? (
            <div className="p-4 text-center text-gray-500">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" />
              Loading clients...
            </div>
          ) : clients.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No at-risk clients found
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {clients.map(client => {
                const level = getRiskLevel(client.riskScore);
                const config = RISK_LEVELS[level];

                return (
                  <Link
                    key={client.id}
                    to={`/clients/${client.id}`}
                    className="flex items-center gap-3 p-3 hover:bg-white transition-colors"
                  >
                    <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center text-white font-bold`}>
                      {client.riskScore}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">
                        {client.firstName} {client.lastName}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        {client.carrier && <span>{client.carrier}</span>}
                        {client.topRiskFactor && (
                          <>
                            <span className="text-gray-300">|</span>
                            <span className="text-orange-600">{client.topRiskFactor}</span>
                          </>
                        )}
                      </div>
                    </div>

                    {client.lastContactDate && (
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock size={12} />
                        {new Date(client.lastContactDate).toLocaleDateString()}
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      {client.phone && (
                        <a
                          href={`tel:${client.phone}`}
                          onClick={e => e.stopPropagation()}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                        >
                          <Phone size={16} />
                        </a>
                      )}
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </Link>
                );
              })}

              {clients.length >= 20 && (
                <button
                  onClick={() => onViewClients(agent.agentId)}
                  className="w-full p-3 text-center text-sm text-blue-600 hover:bg-white"
                >
                  View all {agent.totalClients} clients
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Summary Stats Component
function RiskSummaryStats({ totals, loading }) {
  const stats = [
    { label: 'Critical', count: totals?.critical || 0, config: RISK_LEVELS.critical },
    { label: 'High', count: totals?.high || 0, config: RISK_LEVELS.high },
    { label: 'Elevated', count: totals?.elevated || 0, config: RISK_LEVELS.elevated },
    { label: 'Moderate', count: totals?.moderate || 0, config: RISK_LEVELS.moderate }
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {stats.map(({ label, count, config }) => (
        <div
          key={label}
          className={`${config.bgLight} rounded-xl p-4 border ${config.color.replace('bg-', 'border-')}/30`}
        >
          {loading ? (
            <div className="animate-pulse">
              <div className="h-8 bg-white/50 rounded w-12 mb-2"></div>
              <div className="h-4 bg-white/50 rounded w-16"></div>
            </div>
          ) : (
            <>
              <div className={`text-3xl font-bold ${config.textColor}`}>{count}</div>
              <div className={`text-sm ${config.textColor} opacity-80`}>{label}</div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// Main Risk Center Component
export default function AgencyRiskCenter() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [riskData, setRiskData] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(searchParams.get('level') || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  // Load risk summary data
  useEffect(() => {
    async function loadRiskData() {
      setLoading(true);
      try {
        const res = await fetch(`${BASE}/agency/risk-summary`, { credentials: 'include' });
        if (res.status === 403) {
          navigate('/dashboard');
          return;
        }
        const data = await res.json();
        if (data.ok) {
          setRiskData(data);
        }
      } catch (err) {
        console.error('Failed to load risk data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadRiskData();
  }, [navigate]);

  // Update URL when level changes
  useEffect(() => {
    if (selectedLevel && selectedLevel !== 'all') {
      setSearchParams({ level: selectedLevel });
    } else {
      setSearchParams({});
    }
  }, [selectedLevel, setSearchParams]);

  // Filter agents based on search
  const filteredAgents = riskData?.agents?.filter(agent => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return agent.agentName?.toLowerCase().includes(query) ||
           agent.agentEmail?.toLowerCase().includes(query);
  }) || [];

  // Sort agents by at-risk count
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    const aRisk = (a.riskBreakdown?.critical || 0) + (a.riskBreakdown?.high || 0);
    const bRisk = (b.riskBreakdown?.critical || 0) + (b.riskBreakdown?.high || 0);
    return bRisk - aRisk;
  });

  const handleViewAllClients = (agentId) => {
    navigate(`/agency/clients?agentId=${agentId}&riskLevel=${selectedLevel}`);
  };

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <Link
              to="/agency/dashboard"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
            >
              <ArrowLeft size={16} />
              Back to Dashboard
            </Link>
            <div className="flex items-center gap-3">
              <AlertTriangle size={28} className="text-red-600" />
              <h1 className="text-2xl sm:text-3xl font-bold text-[#172A3A]">
                Risk Center
              </h1>
            </div>
            <p className="text-gray-600 mt-1">
              At-risk clients by agent - click to expand and take action
            </p>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="p-2 hover:bg-white rounded-lg transition-colors self-start"
            title="Refresh"
          >
            <RefreshCw size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Summary Stats */}
        <RiskSummaryStats totals={riskData?.totals} loading={loading} />

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search agents..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Risk Level Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={18} className="text-gray-400" />
            <select
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All At-Risk (35+)</option>
              <option value="critical">Critical (85+)</option>
              <option value="high">High (65-84)</option>
              <option value="elevated">Elevated (50-64)</option>
            </select>
          </div>
        </div>

        {/* Agent List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl shadow p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-48"></div>
                  </div>
                  <div className="h-8 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedAgents.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-8 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No agents found' : 'No agents in your agency'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Try adjusting your search'
                : 'Add agents to start tracking risk across your book of business'
              }
            </p>
            {!searchQuery && (
              <Link
                to="/agency/agents"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:opacity-90"
              >
                <Users size={18} />
                Manage Agents
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {sortedAgents.map(agent => (
              <AgentRiskCard
                key={agent.agentId}
                agent={agent}
                selectedLevel={selectedLevel}
                onViewClients={handleViewAllClients}
              />
            ))}
          </div>
        )}

        {/* Total Summary */}
        {riskData?.totals && (
          <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between">
            <div className="text-gray-600">
              <span className="font-semibold text-gray-900">{sortedAgents.length}</span> agents
              {' '}managing{' '}
              <span className="font-semibold text-gray-900">{riskData.totals.totalClients}</span> total clients
            </div>
            <Link
              to="/agency/clients"
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              View all clients <ChevronRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
